import { useEffect, useMemo, useRef, useState } from "react";

import ProtoAvatar from "components/Screens/Marketplace/Customer/common/ProtoAvatar/ProtoAvatar";
import { useShortlist } from "components/Screens/Marketplace/Customer/Shortlist/ShortlistContext";
import { getTalentDisplayName } from "components/Screens/Marketplace/Customer/utils/redaction";
import { nextWeekdays, timeZoneForState, toIsoDate } from "components/Screens/Marketplace/Customer/utils/availability";
import useTalentApi from "components/Screens/Marketplace/Customer/TalentApiContext";
import { MEETING_ALREADY_REQUESTED, submitMeetingRequest } from "plg/api/plgTalentApi";
import MeetingIcon from "plg/meeting/MeetingIcon";
import PlgIcon from "plg/shell/PlgIcon";
import { getPlgProfile } from "plg/auth/plgTokenStore";
import {
  DEFAULT_FROM,
  DEFAULT_TO,
  EMR_ACCESS,
  fmtCallDay,
  fmtMinutes,
  fmtWindow,
  FROM_MINUTES,
  RECORDS_SYSTEM,
  TO_MINUTES,
  VOIP,
  VOIP_HINT,
} from "plg/meeting/meetingOptions";
import { trackPlg } from "plg/analytics/events";
import { setPlgTraits } from "plg/analytics/identity";
import { urgencyRequestSlug, voipSlug } from "plg/analytics/slugs";
import "plg/meeting/Meeting.scss";
import experienceLabel from "components/Screens/Marketplace/Customer/utils/experienceLabel";
import { isUnavailable } from "components/Screens/Marketplace/Customer/Shortlist/UnavailablePill";
import { standardTilesClicked } from "plg/analytics/plgAnalyticsSession";

export type MeetingStep = "form" | "done" | "sent";

interface Props {
  step: MeetingStep;
  onStep: (step: MeetingStep) => void;
  /** Closes the panel; with a route, goes there too. */
  onClose: (to?: string) => void;
  onSent: () => void;
}

function Tile({ on, label, onClick }: { on: boolean; label: string; onClick: () => void }) {
  return (
    <button type="button" className={`plg-mtg-tile${on ? " is-on" : ""}`} aria-pressed={on} onClick={onClick}>
      <span className="plg-mtg-tick">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M5 12l5 5 9-10" />
        </svg>
      </span>
      {label}
    </button>
  );
}

function Done({ title, text, children }: { title: string; text: string; children: React.ReactNode }) {
  return (
    <div className="plg-mtg-done">
      <div className="plg-mtg-done__icon">
        <MeetingIcon name="success" size={26} />
      </div>
      <h3>{title}</h3>
      <p>{text}</p>
      <div className="plg-mtg-done__actions">{children}</div>
    </div>
  );
}

/** Prototype panel 12: "Request a meeting", the same with or without a shortlist. */
function MeetingPanel({ step, onStep, onClose: closePanel, onSent }: Props) {
  const { items: shortlisted } = useShortlist();
  // Only people still on the talent network: someone who has since been placed (e.g. ASSIGNED) is not walked through.
  const items = useMemo(() => shortlisted.filter((i) => !isUnavailable(i.talent)), [shortlisted]);
  const api = useTalentApi();
  // Two weeks of weekdays from tomorrow
  const days = useMemo(() => nextWeekdays(10), []);
  // The urgency question was removed (product 2026-11-03): the request and the analytics event leave it out.
  const urgency = "";
  const [voip, setVoip] = useState("");
  const [emr, setEmr] = useState("");
  const [day, setDay] = useState(() => toIsoDate(days[0]));
  const [from, setFrom] = useState(DEFAULT_FROM);
  const [to, setTo] = useState(DEFAULT_TO);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(false);
  // Over 3 people the list scrolls on its own; the bottom fade shows there is more until the end is reached.
  const listScrolls = items.length > 3;
  const [listEnd, setListEnd] = useState(false);
  const tz = timeZoneForState(api.customerState);
  // EMR for Medical and Dental, AMS for Insurance; no access question for any other industry
  const records = RECORDS_SYSTEM[getPlgProfile()?.industry || ""];
  const lastTouched = useRef<string | null>(null);
  const touch = (field: string) => {
    lastTouched.current = field;
  };

  // Closing the form without sending is meeting_panel_abandoned (which field they stopped on).
  const onClose = (to?: string) => {
    if (step === "form")
      trackPlg("meeting_panel_abandoned", { shortlist_count: items.length, last_field_touched: lastTouched.current });
    closePanel(to);
  };

  // The page under the panel never scrolls: a wheel over the head, foot or scrim scrolls the panel's body instead,
  // and the body's own scroll stops at its ends (overscroll-behavior in Meeting.scss).
  const rootRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const canScroll = (el: HTMLElement) => el.scrollHeight > el.clientHeight;
    const onWheel = (e: WheelEvent) => {
      const body = bodyRef.current;
      if (body && body.contains(e.target as Node) && canScroll(body)) return;
      e.preventDefault();
      if (body) body.scrollTop += e.deltaY;
    };
    const onTouchMove = (e: TouchEvent) => {
      const body = bodyRef.current;
      if (!body || !body.contains(e.target as Node) || !canScroll(body)) e.preventDefault();
    };
    root.addEventListener("wheel", onWheel, { passive: false });
    root.addEventListener("touchmove", onTouchMove, { passive: false });
    return () => {
      root.removeEventListener("wheel", onWheel);
      root.removeEventListener("touchmove", onTouchMove);
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Every answer may be skipped; only the call window has to make sense.
  const ok = !!day && to > from;

  const send = async () => {
    if (!ok || sending) return;
    setSending(true);
    setError(false);
    const ids = items.map((i) => i.talent._id);
    const res = await submitMeetingRequest({
      ...(urgency ? { urgency } : {}),
      ...(voip ? { voip } : {}),
      ...(emr ? { emrAccess: emr } : {}),
      slot: { date: day, time: fmtWindow(from, to), timeZone: tz },
      ...(note.trim() ? { note: note.trim() } : {}),
      edgeStandardTilesClicked: standardTilesClicked(),
    });
    setSending(false);
    if (res.ok === false && res.code === MEETING_ALREADY_REQUESTED) {
      // Sent before (another tab or device): say so instead of asking again.
      // Sent before (another tab or device): close; the buttons now read Meeting requested (product 2026-11-04).
      onSent();
      onClose();
      return;
    }
    if (res.ok === false) {
      setError(true);
      trackPlg("error_shown", { error_type: res.code || "meeting_request_failed", screen: "meeting_panel" });
      return;
    }
    trackPlg("meeting_requested", {
      shortlist_count: ids.length,
      shortlist_ids: ids,
      urgency_request: urgencyRequestSlug(urgency),
      has_voip: voipSlug(voip),
      notes_given: !!note.trim(),
      standard_tiles_clicked: standardTilesClicked(),
    });
    const hasVoip = voipSlug(voip);
    if (hasVoip) setPlgTraits({ has_voip: hasVoip });
    onSent();
    onStep("done");
  };

  let title = "Request a meeting";
  let body: React.ReactNode = null;
  let foot: React.ReactNode = null;

  if (step === "form") {
    body = (
      <>
        <div className="plg-mtg-sec" style={{ marginTop: 0 }}>
          {items.length ? (
            <>
              <h3>Your shortlist</h3>
              <div
                className={`plg-mtg-list${listScrolls ? " plg-mtg-list--scroll" : ""}${listEnd ? " is-end" : ""}`}
                onScroll={(e) => {
                  const el = e.currentTarget;
                  setListEnd(el.scrollTop + el.clientHeight >= el.scrollHeight - 1);
                }}
              >
                {items.map(({ talent }) => (
                  // eslint-disable-next-line no-underscore-dangle
                  <div className="plg-mtg-person" key={talent._id}>
                    <ProtoAvatar talent={talent} size={36} />
                    <div>
                      <b>{getTalentDisplayName(talent)}</b>
                      <span>{talent.roles?.[0] || experienceLabel(talent.totalExperience)}</span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="plg-mtg-honest">
                Your shortlist tells us what good looks like. We suggest a list based on availability.
              </p>
            </>
          ) : (
            <div className="plg-mtg-empty">
              <b>Help us find the right person for you</b>
              <p>
                The more you shortlist, the better we understand what a great fit looks like for you. A few picks before
                the meeting go a long way.
              </p>
              <button type="button" className="plg-mtg-link" onClick={() => onClose(api.routes.talentNetwork)}>
                Browse
              </button>
            </div>
          )}
        </div>
        {/* "When do you need someone in place?" is no longer asked (product 2026-11-03). To bring it back, restore the
            urgency state and the URGENCY_MEETING import, and uncomment this block.
        <div className="plg-mtg-sec">
          <h3>When do you need someone in place?</h3>
          <div className="plg-mtg-tiles">
            {URGENCY_MEETING.map((x) => (
              <Tile
                key={x}
                on={urgency === x}
                label={x}
                onClick={() => {
                  touch("urgency");
                  setUrgency(urgency === x ? "" : x);
                }}
              />
            ))}
          </div>
        </div>
        */}
        <div className="plg-mtg-group">
          <div className="plg-mtg-kicker">Your setup</div>
          <div className="plg-mtg-sec">
            <h3>Do you have a VoIP system?</h3>
            <div className="plg-mtg-tiles plg-mtg-tiles--one">
              {VOIP.map((x) => (
                <Tile
                  key={x}
                  on={voip === x}
                  label={x}
                  onClick={() => {
                    touch("voip");
                    setVoip(voip === x ? "" : x);
                  }}
                />
              ))}
            </div>
            <p className="plg-mtg-hint">{VOIP_HINT}</p>
          </div>
          {records && (
            <div className="plg-mtg-sec">
              <h3>How does your team access your {records}?</h3>
              <div className="plg-mtg-tiles plg-mtg-tiles--one">
                {EMR_ACCESS.map((x) => (
                  <Tile
                    key={x}
                    on={emr === x}
                    label={x}
                    onClick={() => {
                      touch("emr");
                      setEmr(emr === x ? "" : x);
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="plg-mtg-sec">
          <h3>When are you free for a call?</h3>
          <div className="plg-mtg-slot">
            <label className="plg-mtg-slot__part">
              I&apos;m available on
              <select
                className="plg-mtg-select"
                aria-label="Day"
                value={day}
                onChange={(e) => {
                  touch("day");
                  setDay(e.target.value);
                }}
              >
                {days.map((d) => (
                  <option key={toIsoDate(d)} value={toIsoDate(d)}>
                    {fmtCallDay(d)}
                  </option>
                ))}
              </select>
            </label>
            <label className="plg-mtg-slot__part">
              from
              <select
                className="plg-mtg-select"
                aria-label="From"
                value={from}
                onChange={(e) => {
                  const m = Number(e.target.value);
                  touch("time");
                  setFrom(m);
                  // "To" stays after "from": an hour later when the new start passes it.
                  if (to <= m) setTo(Math.min(m + 60, TO_MINUTES[TO_MINUTES.length - 1]));
                }}
              >
                {FROM_MINUTES.map((m) => (
                  <option key={m} value={m}>
                    {fmtMinutes(m)}
                  </option>
                ))}
              </select>
            </label>
            <label className="plg-mtg-slot__part">
              to
              <select
                className="plg-mtg-select"
                aria-label="To"
                value={to}
                onChange={(e) => {
                  touch("time");
                  setTo(Number(e.target.value));
                }}
              >
                {TO_MINUTES.filter((m) => m > from).map((m) => (
                  <option key={m} value={m}>
                    {fmtMinutes(m)}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="plg-mtg-hint">
            Times are in your local time zone. The call takes around 15 minutes, so a wider window makes it easier to
            find a time.
          </div>
        </div>
        <div className="plg-mtg-sec">
          <h3>
            Anything else we should know? <span className="plg-mtg-opt">Optional</span>
          </h3>
          <textarea
            className="plg-mtg-text"
            value={note}
            onChange={(e) => {
              touch("notes");
              setNote(e.target.value);
            }}
            placeholder="Shift times, specialisations, anything that matters to you"
          />
        </div>
      </>
    );
    foot = (
      <>
        {error && <p className="plg-mtg-hint plg-mtg-error">Something went wrong. Please try again.</p>}
        <button
          type="button"
          className="plg-mtg-btn plg-mtg-btn--primary plg-mtg-btn--block"
          disabled={!ok || sending}
          onClick={send}
        >
          Request a meeting
        </button>
      </>
    );
  } else if (step === "done") {
    // What was just sent: the call window and how many profiles went with it
    const name = getPlgProfile()?.firstName?.trim();
    const picked = days.find((d) => toIsoDate(d) === day);
    const n = items.length;
    body = (
      <div className="plg-mtg-sent">
        <div className="plg-mtg-sent__hero">
          <button type="button" className="plg-mtg-x plg-mtg-sent__x" aria-label="Close" onClick={() => onClose()}>
            <MeetingIcon name="x" />
          </button>
          <div className="plg-mtg-sent__icon">
            <PlgIcon name="calTick" size={26} />
          </div>
          <h3>{name ? `Thanks, ${name}` : "Thanks"}</h3>
          <p>We&apos;re already pulling profiles that match what you told us.</p>
        </div>
        <dl className="plg-mtg-sent__rows">
          <div className="plg-mtg-sent__row">
            <dt>You&apos;re available on</dt>
            <dd>
              <b>{picked ? fmtCallDay(picked) : ""}</b>
              <span>{`${fmtMinutes(from)} to ${fmtMinutes(to)}`}</span>
            </dd>
          </div>
          <div className="plg-mtg-sent__row">
            <dt>Your shortlist</dt>
            <dd>
              <b>{n ? `${n} ${n === 1 ? "profile" : "profiles"}` : "Nothing saved yet"}</b>
              <span>{n ? "saved so far" : "We'll bring suggestions to the call"}</span>
            </dd>
          </div>
        </dl>
        <div className="plg-mtg-sent__actions">
          <button type="button" className="plg-mtg-btn plg-mtg-btn--primary plg-mtg-btn--block" onClick={() => onClose()}>
            Continue browsing
          </button>
        </div>
      </div>
    );
  } else {
    body = (
      <Done title="Already requested" text="Your meeting request is with Edge. We will be in touch to arrange a time.">
        <button type="button" className="plg-mtg-btn plg-mtg-btn--primary" onClick={() => onClose()}>
          Continue
        </button>
      </Done>
    );
  }

  return (
    <div className="plg-mtg" ref={rootRef}>
      <div className="plg-mtg-scrim" onClick={() => onClose()} role="presentation" />
      <aside className={`plg-mtg-panel${step === "done" ? " plg-mtg-panel--sent" : ""}`} role="dialog" aria-label={title}>
        {/* The confirmation carries its own close button in its header band */}
        {step !== "done" && (
          <div className="plg-mtg-head">
            <h2>{title}</h2>
            <button type="button" className="plg-mtg-x" aria-label="Close" onClick={() => onClose()}>
              <MeetingIcon name="x" />
            </button>
          </div>
        )}
        <div className="plg-mtg-body" ref={bodyRef}>
          {body}
        </div>
        {foot && <div className="plg-mtg-foot">{foot}</div>}
      </aside>
    </div>
  );
}

export default MeetingPanel;
